import React, { useState } from 'react';
import {FileSerializableItem} from '@/interface';
import {Button, Icon} from '@/ui-kits';
import {FileViewerModal} from '../FileViewerModal';
import {cn, downloadFileFromUrl, fetchAndDownloadFile, formatFileSize} from '@/utils/common_functions';
import { Loader2 } from 'lucide-react';
import { API, PatientAccountTicketRequestStatus } from '@/constants';
import { useSelector } from 'react-redux';
import { accountDeletionStatusSelector } from '@/services/redux/selectors';
import { getDocumentBytes } from '@/services/api';

type actions = 'delete' | 'download' | 'view';
interface FilePreviewProps {
  file: FileSerializableItem;
  handleDelete?: (id: FileSerializableItem['id']) => void;
  handleView?: (file: FileSerializableItem) => void;
  disabled?: boolean;
  bgVariant?: 'grey' | 'blue';
  allowed_actions: actions[];
  readonly?: boolean;
  error?: boolean;
  openInModal?: boolean;
  loading?: boolean;
}
export const FilePreview: React.FC<FilePreviewProps> = props => {
  const {
    file,
    handleDelete,
    handleView,
    disabled,
    readonly = false,
    openInModal = true,
    allowed_actions = ['view', 'delete'],
    bgVariant = 'blue',
    error,
    loading = false,
  } = props;
  const [viewerOpen, setViewerOpen] = React.useState(false);
  const accountDeletionStatus = useSelector(accountDeletionStatusSelector);

  async function handleDownload(fileId: number, filename: string) {
    let blob = await getDocumentBytes(fileId);
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename ?? '';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(objectUrl);
  }
  return (
    <div
      key={file.id}
      className={cn(
        'flex items-start sm:items-center justify-between rounded-lg p-2 sm:p-3 gap-2',
        bgVariant === 'grey' ? 'bg-ui_form_bg' : 'bg-ui_sky_blue border border-ui_input_border_dark/30',
        error && 'bg-red-50 border-red-300',
      )}>
      {bgVariant === 'blue' && <Icon type={error ? 'failure' : 'file_attach'} className="size-5 text-ui_blue" />}
      <div className="min-w-0 flex-1">
        <p className="text-xs sm:text-sm font-medium text-ui_input_border_dark break-words leading-tight">{file.name}</p>
        <p className="text-xs text-ui_input_border_dark mt-0.5">{formatFileSize(file.size)}</p>
      </div>
      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 mt-0.5 sm:mt-0">
        <Button
          title="View"
          variant="secondary"
          size="sm"
          className="text-xs px-4 py-[2px] sm:text-sm"
          onClick={() => {
            if (openInModal) {
              setViewerOpen(true);
              return;
            }
            handleView?.(file);
          }}
        />

        {loading && <Loader2 className="size-4 animate-spin" />}
        {!loading && !readonly && allowed_actions.includes('delete') && accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && (
          <button disabled={disabled} onClick={() => handleDelete?.(file.id)} className="disabled:opacity-50 p-1">
            <Icon type="trash_red" className="size-4" />
          </button>
        )}

        {!loading && allowed_actions.includes('download') && (
          <button onClick={() => handleDownload(file.id, file.name)} className="text-ui_blue hover:text-ui_blue_light p-1">
            <Icon type="download_btn_nobg" className="size-4" />
          </button>
        )}
      </div>
      <FileViewerModal isOpen={viewerOpen} onClose={() => setViewerOpen(false)} file={file} showDownloadButton={allowed_actions.includes('download')} />
    </div>
  );
};
